import React from "react";
import axios from "axios";
import { API_get_plan_task_analytics } from "../../../../constants";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import {
  SummaryTiles,
  StackedTrend,
  EmployeeTable,
  StoreTable,
} from "./reportComponents";
import AuditReport from "./AuditReport";

// Reports level 2: one task's outcomes over the same scope the scoreboard was
// showing, with store and employee breakdowns.

function TaskReportDetail({ task, scope, homeCompany, homeStore, onBack, onOpenOccurrence }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const axiosConfig = await getDefaultAxiosConfig();
        const storeParam = scope?.allLocations ? "" : `&store_ids=${homeStore}`;
        const res = await axios.get(
          `${API_get_plan_task_analytics}?company_id=${homeCompany}&plan_task_uuid=${task.uuid}&date_start=${scope.dateStart}&date_end=${scope.dateEnd}${storeParam}`,
          axiosConfig
        );
        if (!cancelled) setData(res.data?.redux?.payload || null);
      } catch (e) {
        if (!cancelled) setData(null);
        console.error("Error fetching task report detail:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [task?.uuid, homeCompany, homeStore, scope?.dateStart, scope?.dateEnd, scope?.allLocations]);

  return (
    <div>
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg px-3 py-1.5 bg-white hover:border-slate-400"
        >
          ‹ All tasks
        </button>
        <div>
          <span className="text-base font-bold text-slate-900">{task.name}</span>
          <span className="text-xs text-slate-400 ml-2">
            {[task.category, `${scope.dateStart} to ${scope.dateEnd}`]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <LoadSpinner />
        </div>
      ) : !data ? (
        <div className="border border-slate-200 rounded-xl bg-white p-10 text-center mt-4 text-sm text-slate-500">
          Could not load this task's report.
        </div>
      ) : (
        <>
          {data.audit && (

            <div className="mt-4">
              <AuditReport
                audit={data.audit}
                homeStore={homeStore}
                onOpenAudit={
                  onOpenOccurrence ? (occ) => onOpenOccurrence(task.uuid, occ.date) : undefined
                }
              />
            </div>
          )}
          <div className="mt-4">
            <SummaryTiles summary={data.summary} />
          </div>

          <div className="mt-4">
            <StackedTrend dailyTrend={data.daily_trend} title="Daily outcomes" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-4 mt-4 items-start">
            <EmployeeTable rows={data.employee_breakdown} title="Completed by" />
            <StoreTable rows={data.store_breakdown} />
          </div>
        </>
      )}
    </div>
  );
}

export default TaskReportDetail;
